import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { SearchDropdown } from '../searchDropdown/SearchDropdown';
import { Button } from '@app/components/common/buttons/Button/Button';
import { useResponsive } from '@app/hooks/useResponsive';
import { useNavigationContext } from '@app/contexts/NavigationContext';
import { SidebarNavigationItem } from '@app/components/layouts/main/sider/sidebarNavigation';
import * as S from './HeaderSearch.styles';

export interface CategoryComponents {
  category: string;
  components: {
    name: string;
    title: string;
    url: string;
    categories: string[];
    keywords: string[];
  }[];
}

const flattenItems = (
  items: SidebarNavigationItem[]
): { title: string; url: string }[] =>
  items.flatMap((item) =>
    item.url
      ? [{ title: item.title, url: item.url }]
      : item.children
      ? flattenItems(item.children)
      : []
  );

export const HeaderSearch: React.FC = () => {
  const { mobileOnly, isTablet } = useResponsive();
  const { pathname } = useLocation();
  const { navigation } = useNavigationContext();

  const [query, setQuery] = useState('');
  const [isModalVisible, setModalVisible] = useState(false);
  const [isOverlayVisible, setOverlayVisible] = useState(false);

  const sortedResults = useMemo((): CategoryComponents[] | null => {
    if (!query.trim() || !navigation.length) return null;

    const q = query.toLowerCase();

    const groups = navigation.reduce<CategoryComponents[]>((acc, topLevel) => {
      const leaves = topLevel.url
        ? [{ title: topLevel.title, url: topLevel.url }]
        : flattenItems(topLevel.children ?? []);

      const matches = leaves.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.url.toLowerCase().includes(q)
      );

      if (matches.length) {
        acc.push({
          category: topLevel.title,
          components: matches.map((item) => ({
            name: item.title,
            title: item.title,
            url: item.url,
            categories: [topLevel.title],
            keywords: [item.title.toLowerCase()],
          })),
        });
      }

      return acc;
    }, []);

    return groups.length > 0 ? groups : null;
  }, [query, navigation]);

  useEffect(() => {
    setModalVisible(false);
    setOverlayVisible(false);
  }, [pathname]);

  return (
    <>
      {mobileOnly && (
        <>
          <Button
            type={isModalVisible ? 'ghost' : 'text'}
            icon={<S.SearchIcon onClick={() => setModalVisible(true)} />}
          />
          <S.SearchModal
            visible={isModalVisible}
            closable={false}
            footer={null}
            onCancel={() => setModalVisible(false)}
            destroyOnClose
          >
            <SearchDropdown
              query={query}
              setQuery={setQuery}
              data={sortedResults}
              isOverlayVisible={isOverlayVisible}
              setOverlayVisible={setOverlayVisible}
            />
          </S.SearchModal>
        </>
      )}

      {isTablet && (
        <SearchDropdown
          query={query}
          setQuery={setQuery}
          data={sortedResults}
          isOverlayVisible={isOverlayVisible}
          setOverlayVisible={setOverlayVisible}
        />
      )}
    </>
  );
};
